// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { HceFileManagementService, HceResponseType } from '../hce-file-management/hce-file-management.service';
import { Observable, of, map, switchMap, throwError } from 'rxjs';
import { HceRefGeneExonRaw, HceView } from '../../../interfaces/hce/hce-reference';
import { FileMeta, HceApiService } from '../hce-api/hce-api.service';
import { Sv, Type, Zygosity } from '../../../interfaces/hce/hce-sv';
import {
  HceSvFilter,
  SvFilterPhysicalPosition,
  SvFilterSvLength,
  SvFilterSvType,
  SvFilterZygosity,
  SvFilterSvReason,
  SvFilterMatchAnnotation,
} from '../../../interfaces/hce/hce-sv-filter';
import { HceAnnotation } from '../../../interfaces/hce/hce-annotation';
import { DEFAULT_POSITION_MAX } from '../../../shared/constants/hce/hce-sv-filter-constants';

/** Simple numeric range */
type Range = { min?: number; max?: number };

/** Normalized values (types easy to compare) */
type NormPos = { chrId: number; start?: number; end?: number; };
type NormLen = { min?: number; max?: number; };
type NormMatchAnnotation = { file: string; unit: 'bp' | '%'; threshold: number; excludeMatches: boolean; };
type Dict<T> = Record<string, T>;
type OverlapCache = Record<string /* svKey */, Record<string /* file */, number[]>>;

type NormFilter = {
  positions: NormPos[];
  positionsHide: NormPos[];
  lengths: NormLen[];
  types: Set<Type> | null;
  zygosity: Set<Zygosity> | null;
  reasons: Set<SvFilterSvReason> | null;
  includeEmbedded: boolean;
  geneTokens: string[];
  requireRefGenes: boolean;
  minIntervalDepth: Range;
  medianIntervalDepth: Range;
  qualityScore: Range;
  breakpointPass?: boolean;
  breakpointFail?: boolean;
  coLoRSdBControlDb?: number;
  gnomADControlDb?: number;
  ontLRSControlDb?: number;
  excludeMasked: boolean;
  matchAnnotations: NormMatchAnnotation[];
  bestSVperCluster?: boolean;
};

@Injectable({ providedIn: 'root' })
export class HceSvFilterService {
  private _refNameMap: Record<string, string> = {};
  private overlapCache: OverlapCache = {};

  /** Extracts key names as an array (supports const objects) */
  getKeys<TObj extends Record<string, string>>(obj: TObj) {
    return Object.keys(obj) as Array<keyof TObj>;
  }

  /** Initializes a Record<key, boolean> with all values set to false */
  buildInitialSelectedByKey<TObj extends Record<string, string>>(
    obj: TObj
  ): Record<keyof TObj, boolean> {
    return this.getKeys(obj).reduce((acc, k) => {
      acc[k] = false;
      return acc;
    }, {} as Record<keyof TObj, boolean>);
  }

  /** Get the default filter settings. */
  getDefaultFilter(): HceSvFilter {
    return {
      svLength: [
        {
          lenMin: 300,
          lenMinBp: 1,
          lenMax: DEFAULT_POSITION_MAX,
          lenMaxBp: 1
        }
      ],
      minIntervalDepth: {
        min: 8
      },
      svBreakpointMoleculeCheck: {
        pass: true,
        fail: false
      },
      svsBelowPresenceControlDb: {
        coLoRSdBControlDb: 0.01,
        gnomADControlDb: 0.01,
        ontLRSControlDb: 0.01
      },
      masked: {
        excludeMasked: true
      }, 
      bestSVperCluster: {
        showBestSVperCluster: true
      }
    };
  }
  
  /** Applies filter to the SV dictionary (pure function) */
  filterSV(
    svs: Dict<Sv>, 
    refNameMap: Record<string, string>, 
    filter: HceSvFilter, 
    annotationsByFile?: Record<string, 
    HceAnnotation[]>
  ): Dict<Sv> {
    const nf = this.compile(filter);
    this._refNameMap = refNameMap;
    return Object.fromEntries(
      Object.entries(svs).filter(([key, sv]) => this.matchesSV(sv, nf, annotationsByFile, key))
    ) as Dict<Sv>;
  }

  filterAnnotation(
    annotationsByFile: Record<string, HceAnnotation[]>,
    refNameMap: Record<string, string>, 
    filter: HceSvFilter,
    selectedFiles: string[],
  ): Record<string, HceAnnotation[]> {
    const nf = this.compile(filter);
    this._refNameMap = refNameMap;

    return Object.fromEntries(
      selectedFiles.map(file => [
        file,
        (annotationsByFile[file] ?? []).filter(anno =>
          this.matchesAnnotation(anno, nf)
        ),
      ])
    );
  }

  filterChrList(
    baseRefsMappingTable: Record<string, string>, 
    filter_physicalPosition_chr? : number[]
  ) : Record<string, string> {
    return Object.fromEntries(
        Object.entries(baseRefsMappingTable).filter(([chr_id, chr]) => 
          filter_physicalPosition_chr ? filter_physicalPosition_chr.includes(parseInt(chr_id)) : true
        )
    );
  }

  /** Filter normalization (unit conversion to bp, string normalization, unit parsing, etc.) */
  compile(filter: HceSvFilter): NormFilter {
    const unitFactor = (u?: number) => (u && u > 0 ? u : 1); // Expected: 1 | 1000 | 1e6 | 1e9. If 0/undefined → fallback to bp.
    const toBp = (v?: number | string | null, u?: number | null) =>
      v == null || (v as any) === '' ? undefined : Math.round(Number(v) * unitFactor(u ?? undefined));

    const chrToId = (chr: string): number => {
      const s = (chr ?? '').trim().toUpperCase();
      if (s.startsWith('CHR')) return Number(s.slice(3));
      if (s === 'X') return 23;
      if (s === 'Y') return 24;
      const n = Number(s);
      return Number.isFinite(n) ? n : NaN;
    };

    const normPos = (xs?: SvFilterPhysicalPosition[]) =>
      (xs ?? [])
        .map(x => ({ chrId: chrToId(x.chr), start: toBp(x.posStart, x.posBp), end: toBp(x.posEnd, x.posBp) }))
        .filter(p => Number.isFinite(p.chrId) && (p.start != null || p.end != null));

    const normLen = (xs?: SvFilterSvLength[]) =>
      (xs ?? [])
        .map(x => ({ min: toBp(x.lenMin, x.lenMinBp), max: toBp(x.lenMax, x.lenMaxBp) }))
        .filter(l => l.min != null || l.max != null);

    const types = (filter.svType?.length ? new Set(filter.svType) : null) as Set<SvFilterSvType> | null;
    const zSelected = (filter.zygosity?.length ? new Set(filter.zygosity) : null) as Set<Zygosity> | null;
    const reasons = (filter.svReason?.length ? new Set(filter.svReason) : null);

    const matchAnnotations: NormMatchAnnotation[] =
      (filter.matchAnnotation ?? [])
        .map((m: SvFilterMatchAnnotation) => ({
          file: String(m.annotation ?? '').trim(),
          //unit: this.parseThresholdUnit(m.thresholdunit), // 1 = bp, others = interpreted as % (even values such as 1000 are treated as %)
          unit: this.parseThresholdUnit(m.thresholdunit == 'bp' ? 1 : 1000), //★TODO:numberからstringに変更した影響でhce-sv-filter.service.tsにエラーが発生するため、仮対応
          threshold: Number(m.threshold),
          excludeMatches: !!m.excludeMatches,
        }))
        .filter(x => !!x.file && Number.isFinite(x.threshold));

    return {
      positions: normPos(filter.physicalPosition),
      positionsHide: normPos(filter.physicalPositionHide),
      lengths: normLen(filter.svLength),
      types: types as any,
      zygosity: zSelected,
      reasons,
      includeEmbedded: !!filter.flaggedSV?.includeEmbedded,
      geneTokens: (filter.geneName ?? []).map(s => String(s).trim().toUpperCase()).filter(Boolean),
      requireRefGenes: !!filter.svsWithRefGenesDetected?.overlapKG,
      minIntervalDepth: { min: filter.minIntervalDepth?.min, max: filter.minIntervalDepth?.max },
      medianIntervalDepth: { min: filter.medianIntervalDepth?.min, max: filter.medianIntervalDepth?.max },
      qualityScore: { min: filter.qualityScore?.min, max: filter.qualityScore?.max },
      breakpointPass: filter.svBreakpointMoleculeCheck?.pass,
      breakpointFail: filter.svBreakpointMoleculeCheck?.fail,
      coLoRSdBControlDb: filter.svsBelowPresenceControlDb?.coLoRSdBControlDb,
      gnomADControlDb: filter.svsBelowPresenceControlDb?.gnomADControlDb,
      ontLRSControlDb: filter.svsBelowPresenceControlDb?.ontLRSControlDb,
      excludeMasked: !!filter.masked?.excludeMasked,
      bestSVperCluster: filter.bestSVperCluster?.showBestSVperCluster,
      matchAnnotations,
    };
  }

  // Rebuild all precomputed overlap caches
  async buildOverlapCache(
    svs: Record<string, Sv>,
    refNameMap: Record<string, string>,
    selectedAnnotationsByFile: Record<string, HceAnnotation[]>,
    chunkSize = 200,
  ): Promise<void> {
    this._refNameMap = refNameMap;
    const next: OverlapCache = {};
    const annIndex = this.buildAnnotationIndex(selectedAnnotationsByFile);
    const entries = Object.entries(svs);

    for (let i = 0; i < entries.length; i++) {
      const [svKey, sv] = entries[i];

      const s = this.convNum(sv.region?.chr?.start?.min);
      const e = this.convNum(sv.region?.chr?.end?.max);
      const svChrName = this._refNameMap[Number(sv.chr_id)];
      if (!svChrName) continue;

      const byFile: Record<string, number[]> = {};
      for (const [file, byChr] of Object.entries(annIndex)) {
        const annos = byChr[svChrName] ?? [];
        if (!annos?.length) continue;
        // Raw overlap array preserved for legacy UI–compatible "existence determination" using minimum positive > 0
        const overlaps = this.collectOverlapStatsUICompatible(svChrName, s, e, annos);
        byFile[file] = overlaps;
      }
      next[svKey] = byFile;
      // Note: Return control to the main thread for each chunk
      if ((i + 1) % chunkSize === 0) {
        await this.yieldToMain();
      }
    }
    this.overlapCache = next;
  }

  // Clears cache (full reset)
  clearOverlapCache(): void {
    this.overlapCache = {};
  }

  // ================= Core evaluation logic =================
  private matchesSV(sv: Sv, f: NormFilter, annotationsByFile?: Record<string, HceAnnotation[]>, svKey?: string): boolean {
    return (
      this.posInclude(sv, f) &&
      this.posHide(sv, f) &&
      this.svType(sv, f) &&
      this.svLen(sv, f) &&
      this.embedded(sv, f) &&
      this.zyg(sv, f) &&
      this.genes(sv, f) &&
      this.overlapKG(sv, f) &&
      this.depthAndQuality(sv, f) &&
      this.breakpointMolCheck(sv, f) &&
      this.svsBelowPresenceControlDb(sv, f) &&
      this.masked(sv, f) &&
      this.reason(sv, f) &&
      this.bestSvPerCluster(sv, f) &&
      this.matchAnnotation(sv, f, annotationsByFile, svKey)
    );
  }
  
  private matchesAnnotation(anno: HceAnnotation, f: NormFilter): boolean {
    return (
      this.posIncludeAnno(anno, f) &&
      this.posHideAnno(anno, f) &&
      this.svLenAnno(anno, f) &&
      this.zygAnno(anno, f) &&
      this.genesAnno(anno, f) &&
      this.overlapKGAnno(anno, f)
    );
  }

  // ---- Genomic position filtering (include/exclude)
  private posInclude(sv: Sv, f: NormFilter): boolean {
    const xs = f.positions;
    if (!xs?.length) return true;
    return xs.some(c => this.within(sv, c));
  }

  private posIncludeAnno(anno: HceAnnotation, f: NormFilter): boolean {
    const xs = f.positions;
    if (!xs?.length) return true;
    return xs.some(c => this.withinAnno(anno, c));
  }

  private posHide(sv: Sv, f: NormFilter): boolean {
    const xs = f.positionsHide;
    if (!xs?.length) return true;
    return !xs.some(c => this.within(sv, c));
  }

  private posHideAnno(anno: HceAnnotation, f: NormFilter): boolean {
    const xs = f.positionsHide;
    if (!xs?.length) return true;
    return !xs.some(c => this.withinAnno(anno, c));
  }

  private within(sv: Sv, c: NormPos): boolean {
    // Primary chromosome
    if (sv.chr_id === c.chrId) {
      const s = this.num(sv.region?.chr?.start?.min * 1000); // kbp -> bp
      const e = this.num(sv.region?.chr?.end?.max * 1000);   // kbp -> bp
      if (this.rangeContains(s, e, c.start, c.end)){
        return true;
      }
    }
    // Counterpart of translocation
    if (sv.type === 'Translocation' && sv.chr_to_id === c.chrId) {
      const s2 = this.num((sv.segment_to?.start?.min ?? 0) * 1000); // kbp -> bp
      const e2 = this.num((sv.segment_to?.end?.max ?? 0) * 1000);   // kbp -> bp
      if (this.rangeContains(s2, e2, c.start, c.end)) {
        return true;
      }
    }
    return false;
  }

  private withinAnno(anno: HceAnnotation, c: NormPos): boolean {
    // Primary chromosome
    if (this.chrIntoNum(String(anno.noOfChr)) == c.chrId) {
      const s = this.num(anno.start);
      const e = this.num(anno.end);
      if (this.rangeContains(s, e, c.start, c.end)){
        return true;
      }
    }
    return false;
  }

  private chrIntoNum(chr: string): number {
    switch (chr){
      case "X":
        return 23;
      case "Y":
        return 24;
      default:
        return Number(chr);
    }    
  }

  // ---- Type / Length / Embedded / Zygosity / Gene / RefGene
  private svType(sv: Sv, f: NormFilter): boolean {
    return !f.types || f.types.has(sv.type);
  }

  private svLen(sv: Sv, f: NormFilter): boolean {
    const len = this.num(sv.length * 1000); // kbp -> bp
    const xs = f.lengths;
    if (!xs?.length) return true;
    return xs.some(l => {
      if (l.min != null && len < l.min) return false;
      if (l.max != null && len > l.max) return false;
      return true;
    });
  }

  private svLenAnno(anno: HceAnnotation, f: NormFilter): boolean {
    const len = this.num(anno.length);
    const xs = f.lengths;
    if (!xs?.length) return true;
    return xs.some(l => {
      if (l.min != null && len < l.min) return false;
      if (l.max != null && len > l.max) return false;
      return true;
    });
  }
  
  private embedded(sv: Sv, f: NormFilter): boolean {
    return f.includeEmbedded ? true : sv.Embedded !== 'Y';
  }

  private zyg(sv: Sv, f: NormFilter): boolean {
    if (!f.zygosity) return true;
    const z = (sv.zygosity ?? 'Unknown') as Zygosity;
    return f.zygosity.has(z);
  }

  private zygAnno(anno: HceAnnotation, f: NormFilter): boolean {
    if (!f.zygosity) return true;
    const z = (anno.zygosity ?? 'Unknown') as Zygosity;
    return f.zygosity.has(z);
  }

  private genes(sv: Sv, f: NormFilter): boolean {
    const tokens = f.geneTokens;
    if (!tokens.length) return true;
    const genes = [
      ...(sv.region?.chr?.affected_genes ?? []),
      ...(sv.segment_to?.affected_genes ?? []),
    ].map(g => String(g ?? '').toUpperCase());
    if (!genes.length) return false;
    return tokens.some(tok => genes.some(g => g.includes(tok)));
  }

  private genesAnno(anno: HceAnnotation, f: NormFilter): boolean {
    const tokens = f.geneTokens;
    if (!tokens.length) return true;
    const genes = (anno.geneNameOrg ?? []).map(g => String(g ?? '').toUpperCase());
    if (!genes.length) return false;
    return tokens.some(tok => genes.some(g => g.includes(tok)));
  }

  private overlapKG(sv: Sv, f: NormFilter): boolean {
    if (!f.requireRefGenes) return true;
    const geneNameOrg = sv.region?.chr?.affected_genes ?? [];
    return geneNameOrg.length > 0;
  }
  
  private overlapKGAnno(anno: HceAnnotation, f: NormFilter): boolean {
    if (!f.requireRefGenes) return true;
    const geneNameOrg = anno.geneNameOrg ?? [];
    return geneNameOrg.length > 0;
  }

  // ---- Depth / Quality / Breakpoint check
  private depthAndQuality(sv: Sv, f: NormFilter): boolean {
    const cov = sv.region_frags_info?.region_aggregated_stats?.coverage;
    const minDepth = (cov?.min ?? 0);
    const medDepth = (cov?.median ?? 0);
    if (!this.inRange(minDepth, f.minIntervalDepth)) return false;
    if (!this.inRange(medDepth, f.medianIntervalDepth)) return false;
    const qs = sv.quality_score;
    if (!this.inRange(qs, f.qualityScore)) return false;
    return true;
  }

  private breakpointMolCheck(sv: Sv, f: NormFilter): boolean {
    const any = !!f.breakpointPass || !!f.breakpointFail;
    if (!any) return true;
    const mol = sv.lytx?.mol_check;
    // If lytx / masked is not present, do not filter
    if (mol === undefined) return true;
    if (f.breakpointPass && f.breakpointFail) return true;
    if (f.breakpointPass) return mol === true;
    if (f.breakpointFail) return mol === false;
    return true; // Assumed not to reach this text.
  }

  private svsBelowPresenceControlDb(sv: Sv, f: NormFilter): boolean {
    const coLoRSdB = sv.lytx?.percent_presence_lrs_cohort;
    const gnomAD   = sv.lytx?.percent_presence_gnomad_cohort;
    const ontLRS   = sv.lytx?.percent_presence_ont_cohort;
    
    if (coLoRSdB && f.coLoRSdBControlDb && f.coLoRSdBControlDb <= coLoRSdB) {
      return false;
    }
    if (gnomAD && f.gnomADControlDb && f.gnomADControlDb <= gnomAD) {
      return false;
    }
    if (ontLRS && f.ontLRSControlDb && f.ontLRSControlDb <= ontLRS) {
      return false;
    }
    return true;
  }

  // ---- Masked flag check
  private masked(sv: Sv, f: NormFilter): boolean {
    if (!f.excludeMasked) return true;
    const masked = sv.lytx?.masked;
    // If lytx / masked is not present, do not filter
    return masked == null ? true : masked === false;
  }

  // ---- Reason check
  private reason(sv: Sv, f: NormFilter): boolean {
    if (!f.reasons) return true;
    const r = sv.reason as SvFilterSvReason | undefined;
    return r ? f.reasons.has(r) : false;
  }

  // ---- Best Sv Per Cluster check
  private bestSvPerCluster(sv: Sv, f: NormFilter): boolean {
    if (!f.bestSVperCluster) return true;
    const actual = sv.lytx?.rep_sv_in_cluster;
    // If lytx / rep_sv_in_cluster is not present, do not filter
    return actual == null ? true : actual === true;
  }

  // ---- Annotation conditions (AND evaluation)
  private matchAnnotation(
    sv: Sv,
    f: NormFilter,
    annotationsByFile?: Record<string, HceAnnotation[]>,
    svKey?: string
  ): boolean {
    const conds = f.matchAnnotations;
    let totalHit = 0;
    let total_unmatch_display = 0;
    let hasAnnot = svKey ? Object.values(this.overlapCache[svKey] ?? []).some(arr => arr.length > 0) : false;
    if (!conds?.length) return true;

    for (const cond of conds) {
      const { cntHit, unmatch_display } = this.checkOneAnnotationCond(sv, cond, annotationsByFile, svKey);
      totalHit += cntHit;
      if(!unmatch_display) {
        hasAnnot = true; // Note: Just to be safe (usually check with the cache)
      } else {
        total_unmatch_display++;
      }
    }
    return hasAnnot ? conds.length <= totalHit : conds.length == total_unmatch_display;
  }

  private checkOneAnnotationCond(
    sv: Sv,
    cond: NormMatchAnnotation,
    annotationsByFile?: Record<string, HceAnnotation[]>,
    svKey?: string
  ): { cntHit: number; unmatch_display: boolean } {
    const file = cond.file;
    let cntHit = 0;
    let unmatch_display = false;

    // Convert kbp → bp (truncate) and get reference coordinates
    const s = this.convNum(sv.region?.chr?.start?.min);
    const e = this.convNum(sv.region?.chr?.end?.max);

    // 1) Cache reference (zero cost if precomputed)
    const cached = svKey ? this.overlapCache[svKey]?.[file] : undefined;

    // 2) Fallback computation only when cache is absent
    const svChrName = this._refNameMap[Number(sv.chr_id)];
    if (!svChrName) return { cntHit, unmatch_display };
    const overlaps: number[] = cached ?? this.collectOverlapStatsUICompatible(
      svChrName, s, e, annotationsByFile?.[file] ?? []
    );

    if (overlaps.length !== 0) {
      for (const overlap of overlaps) {
        // Legacy UI behavior: if (val.overlap) evaluates true → 0 is ignored, values such as 0.5 are accepted
        if (overlap !== 0) {
          if (cond.unit === 'bp') {
            if (cond.excludeMatches) {
              // Hit when overlap is < threshold
              if (overlap < cond.threshold) cntHit++;
            } else {
              // Hit when overlap is ≥ threshold
              if (overlap >= cond.threshold) cntHit++;
            }
          } else {
            // Percentage evaluation: adjust denominator (svLen=0 → 1)
            const refLen = Math.max(0, e - s);
            const denom = refLen === 0 ? 1 : refLen;

            const ratio = (overlap / denom) * 100;

            if (cond.excludeMatches) {
              if(ratio < cond.threshold) cntHit++;
            } else {
              if(ratio >= cond.threshold) cntHit++;
            }
          }
        }
      }
    } else if (cond.excludeMatches) {
      unmatch_display = true;
    }
    return { cntHit, unmatch_display };
  }

  // ========= Utilities =========
  /** Threshold unit: 1=bp, others are interpreted as percentage (e.g., values like 100 or 1000 are treated as %) */
  private parseThresholdUnit(u: number): 'bp' | '%' {
    return u === 1 ? 'bp' : '%';
  }

  private num(v?: number | null): number { 
    return Math.round(Number(v ?? 0)); 
  }

  private inRange(v: number | null, r: Range): boolean {
    if (v == null) return true;
    if (r.min != null && v < r.min) return false;
    if (r.max != null && v > r.max) return false;
    return true;
  }

  private rangeContains(start?: number, end?: number, cStart?: number, cEnd?: number): boolean {
    if (start == null || end == null) return false;
    const s = Number(start), e = Number(end);
    const cs = cStart ?? -Infinity, ce = cEnd ?? +Infinity;
    return s >= cs && e <= ce;
  }

  /** Converts kbp to bp by truncating fractional part (3 decimal places → integer) */
  private convNum(kbp: number): number {
    const s = String(kbp ?? 0);
    const [a, b = ""] = s.split(".");
    const frac3 = (b + "000").slice(0, 3); // Truncate to 3 decimal digits
    return parseInt(a + frac3, 10);
  }

  /** Iterates annotations of a single file and performs overlap evaluation */
  private collectOverlapStatsUICompatible(
    svChrName: string,
    svStartBp: number,
    svEndBp: number,
    annosOfFile: any[]
  ): number[] {
    let overlaps : number[] = [];

    for (const a of annosOfFile ?? []) {
      if (a?.chromosome !== svChrName) continue;

      const as = Number(a.start);
      const ae = Number(a.end);

      // Overlap evaluation (same behavior as previous UI)
      if (svEndBp >= as && svStartBp <= ae) {
        const os = Math.max(svStartBp, as);
        const oe = Math.min(svEndBp, ae);
        const len = Math.max(0, oe - os);

        overlaps.push(len);
      }
    }
    return overlaps;
  }

  private yieldToMain(): Promise<void> {
    return new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 0);
    });
  }

  private buildAnnotationIndex(
    selectedAnnotationsByFile: Record<string, HceAnnotation[]>
  ): Record<string, Record<string, HceAnnotation[]>> {
    const result: Record<string, Record<string, HceAnnotation[]>> = {};
  
    for (const [file, annos] of Object.entries(selectedAnnotationsByFile ?? {})) {
      const byChr: Record<string, HceAnnotation[]> = {};
      for (const anno of annos ?? []) {
        const chr = anno.chromosome;
        if (!chr) continue;
        (byChr[chr] ??= []).push(anno);
      }
      result[file] = byChr;
    }
  
    return result;
  }
}